import { useMutation, useQueryClient } from 'react-query';
import { httpClient } from 'src/utils/HttpClients';
import type { TSubLocation, TSubLocations } from 'src/types';
import get from 'lodash/get';
import concat from 'lodash/concat';
import orderBy from 'lodash/orderBy';
import { useAppState } from 'src/context';
import { getServerError } from 'src/utils/helpers/errors';

type TCreateSubLocationMutationProps = { locationId: string };

export type TCreateSubLocationPayload = {
    location_id: string | null;
    name: string;
    description: string;
    capacity: {
        capacity: number | null;
        flex: number;
        effective_date: string;
    };
};

export const useCreateSubLocationMutation = ({ locationId }: TCreateSubLocationMutationProps) => {
    const {
        actions: { handleCallNotification },
    } = useAppState();
    const queryClient = useQueryClient();

    const mutationFn = (payload: TCreateSubLocationPayload) =>
        httpClient.post('/sublocations', payload);

    const onMutate = (payload: TCreateSubLocationPayload) => {
        const previousQuery = queryClient.getQueryData<{ data: TSubLocations }>([
            'sub-locations',
            locationId,
        ]);

        queryClient.setQueryData<{ data: TSubLocations }>(
            ['sub-locations', locationId],
            (oldQuery) => {
                const data = get(oldQuery, 'data', [] as TSubLocations);
                const newItem: TSubLocation = {
                    id: '',
                    name: payload.name,
                    description: payload.description,
                    is_office: true,
                    modified_at: '',
                    created_at: '',
                    location_id: 'location_id',
                    archived: false,
                };
                return {
                    ...oldQuery,
                    data: data.length
                        ? concat(data[0], orderBy(concat(data.slice(1), newItem), 'name'))
                        : [newItem],
                };
            }
        );

        return () =>
            queryClient.setQueryData<{ data: TSubLocations } | undefined>(
                ['sub-locations', locationId],
                previousQuery
            );
    };

    const onSettled = async () => {
        await queryClient.invalidateQueries(['sub-locations', locationId]);
    };

    return useMutation(mutationFn, {
        onMutate,
        onError: (error, __, rollback) => {
            handleCallNotification({ message: getServerError(error), type: 'danger' });
            if (!rollback) {
                return;
            }
            rollback();
        },
        onSettled,
        onSuccess: () => {
            handleCallNotification({ message: 'Sub location added successfully!' });
        },
    });
};
